import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircleOutlined as SuccessIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';
import useStructuredData from '../hooks/useStructuredData';

const EmailConfirmed: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getWebPageData } = useStructuredData();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  const success = searchParams.get('success') === 'true';
  const hasSuccessParam = searchParams.has('success');

  const pageTitle = i18n.language === 'en' 
    ? (success ? 'Email Confirmed Successfully' : 'Email Confirmation Failed')
    : (success ? 'Correo Confirmado Exitosamente' : 'Confirmación de Correo Fallida');
  
  const pageDescription = i18n.language === 'en'
    ? (success 
        ? 'Your email has been successfully confirmed. You can now receive legal publication alerts.'
        : 'Email confirmation failed. Please try again or contact support.')
    : (success 
        ? 'Tu correo ha sido confirmado exitosamente. Ahora puedes recibir alertas de publicaciones legales.'
        : 'La confirmación de correo falló. Por favor intenta de nuevo o contacta soporte.');

  const structuredData = getWebPageData(pageTitle, pageDescription, t('routes.emailConfirmed'));
  
  // This page should not be indexed when accessed without proper parameters
  // It's a result page that should only be accessed through the confirmation flow
  const shouldNoIndex = !hasSuccessParam;

  useEffect(() => {
    // Simulate a small delay to show the loading state
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleContinue = () => {
    const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
    if (isAuthenticated) {
      const mentionsRoute = t('routes.mentions');
      navigate(`/${currentLanguage}${mentionsRoute}`);
    } else {
      const homeRoute = t('routes.home');
      navigate(`/${currentLanguage}${homeRoute}`);
    }
  };

  if (isLoading) {
    return (
      <>
        <SEOErrorBoundary fallbackTitle="Email Confirmed - AlertaLegal" fallbackDescription="Email confirmation status for AlertaLegal legal publication alerts">
          <SEO 
            title={pageTitle}
            description={pageDescription}
            structuredData={structuredData}
            hreflangEnabled={true}
            noindex={shouldNoIndex}
          />
        </SEOErrorBoundary>
        <Container maxWidth="sm">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            {t('emailConfirmation.processing')}
          </Typography>
        </Box>
      </Container>
      </>
    );
  }

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Email Confirmed - AlertaLegal" fallbackDescription="Email confirmation status for AlertaLegal legal publication alerts">
        <SEO 
          title={pageTitle}
          description={pageDescription}
          structuredData={structuredData}
          hreflangEnabled={true}
          noindex={shouldNoIndex}
        />
      </SEOErrorBoundary>
      <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            {success ? (
              <>
                <SuccessIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: 'success.main', 
                    mb: 3 
                  }} 
                />
                <Typography variant="h4" component="h1" gutterBottom color="success.main">
                  {t('emailConfirmation.successTitle')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                  {t('emailConfirmation.successMessage')}
                </Typography>
                <Alert severity="success" sx={{ mb: 4, textAlign: 'left' }}>
                  {t('emailConfirmation.successDetails')}
                </Alert>
              </>
            ) : (
              <>
                <ErrorIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: 'error.main', 
                    mb: 3 
                  }} 
                />
                <Typography variant="h4" component="h1" gutterBottom color="error.main">
                  {t('emailConfirmation.errorTitle')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                  {t('emailConfirmation.errorMessage')}
                </Typography>
                <Alert severity="error" sx={{ mb: 4, textAlign: 'left' }}>
                  {t('emailConfirmation.errorDetails')}
                </Alert>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleContinue}
                sx={{ minWidth: 150 }}
              >
                {isAuthenticated 
                  ? t('emailConfirmation.goToDashboard') 
                  : t('emailConfirmation.goToHome')
                }
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {success 
              ? t('emailConfirmation.nextSteps')
              : t('emailConfirmation.needHelp')
            }
          </Typography>
        </Box>
      </Box>
    </Container>
    </>
  );
};

export default EmailConfirmed;